import type { ElementType, ReactNode, CSSProperties } from 'react';
import { cn } from './cn';

/**
 * Minimal Ant Design `Typography` replacement (Title / Text / Paragraph) so we can
 * drop `import { Typography } from 'antd'`. Covers the props actually used in-app.
 */
const LEVEL: Record<number, string> = {
  1: 'text-3xl font-bold tracking-tight',
  2: 'text-2xl font-bold tracking-tight',
  3: 'text-xl font-semibold',
  4: 'text-xl font-semibold',   // page-title role — unified to 20px (was text-lg/18px) across ~55 pages
  5: 'text-base font-semibold', // section/sub-panel header — 16px
};

function Title({ level = 1, children, className, style }: { level?: 1 | 2 | 3 | 4 | 5; children?: ReactNode; className?: string; style?: CSSProperties }) {
  const Tag = (`h${level}` as ElementType);
  return <Tag className={cn(LEVEL[level], 'text-content', className)} style={style}>{children}</Tag>;
}

const TEXT_TYPE: Record<string, string> = {
  secondary: 'text-muted',
  success: 'text-brand-600 dark:text-brand-400',
  warning: 'text-warning-600 dark:text-harvest-300',
  danger: 'text-danger-600 dark:text-danger-500',
};

function Text({ type, strong, italic, underline, delete: del, disabled, children, className, style, ...rest }: {
  type?: 'secondary' | 'success' | 'warning' | 'danger'; strong?: boolean; italic?: boolean; underline?: boolean;
  delete?: boolean; disabled?: boolean; children?: ReactNode; className?: string; style?: CSSProperties;
} & Record<string, unknown>) {
  return (
    <span
      className={cn(type ? TEXT_TYPE[type] : 'text-content', strong && 'font-semibold', italic && 'italic', underline && 'underline', del && 'line-through', disabled && 'text-subtle', className)}
      style={style}
      {...rest}
    >
      {children}
    </span>
  );
}

function Paragraph({ children, className, style, type }: { children?: ReactNode; className?: string; style?: CSSProperties; type?: 'secondary' | 'success' | 'warning' | 'danger' }) {
  return <p className={cn('mb-3 leading-relaxed', type ? TEXT_TYPE[type] : 'text-content', className)} style={style}>{children}</p>;
}

function Link({ children, className, ...rest }: { children?: ReactNode; className?: string } & Record<string, unknown>) {
  return <a className={cn('text-brand-700 hover:text-brand-800 dark:text-brand-400', className)} {...rest}>{children}</a>;
}

export const Typography = Object.assign(
  function TypographyRoot({ children, className }: { children?: ReactNode; className?: string }) {
    return <div className={className}>{children}</div>;
  },
  { Title, Text, Paragraph, Link },
);
